/**
 * Canonical list of Cloze Math grading-policy ids — the single source of truth, kept free of any
 * `@cortex-js/compute-engine` coupling so schemas and studio UI can import it without pulling the
 * (heavy, lazily-loaded) engine into their bundles. The matching predicates live in
 * `mathEquivalence.ts`. Unlike `ClozeOptionType` (duplicated because `shared` can't import `core`),
 * `core` imports these directly from `shared`, so there is no duplicate to keep in sync.
 */
export const MATH_POLICY_IDS = [
  'equivalent',
  'sameForm',
  'simplified',
  'expanded',
  // `combined` — value-correct AND actually computed/combined (numeric: no pending operation in the
  // raw answer; algebraic: simplest form). `partiallyCombined` — like `combined`, but roots (√) and π
  // may stay in exact symbolic form (a calculable root need NOT be evaluated). Which forms may stay
  // symbolic is carried on the literal's `allowSymbolic` array so this general id identifies its
  // specific variant. `reduced` — every numeric coefficient is written in lowest terms, whether a lone
  // value or inside an expression (raw check).
  'combined',
  'partiallyCombined',
  'reduced',
  'decimal',
  'fraction',
  'integer',
  'approx',
  // Bound policies — compare the answer to an author CONSTANT (`bound`), not to the correct answer.
  'greaterThan',
  'greaterOrEqual',
  'lessThan',
  'lessOrEqual'
] as const;

export type MathPolicyId = (typeof MATH_POLICY_IDS)[number];

export const DEFAULT_MATH_POLICY: MathPolicyId = 'equivalent';

/**
 * Which symbolic forms a `partiallyCombined` blank permits to stay un-evaluated. Carried as data on
 * the literal (see {@link MathPolicyLiteral.allowSymbolic}) so the deliberately-general `partiallyCombined`
 * policy id records WHICH exception it means — a future variant is a new value here, not a new
 * ambiguous policy id (PR 18876, Gilad Yaccobi). Extend with e.g. `'trig'`/`'log'` when a variant ships.
 */
export const MATH_SYMBOLIC_EXCEPTIONS = ['roots', 'pi'] as const;
export type MathSymbolicException = (typeof MATH_SYMBOLIC_EXCEPTIONS)[number];

/**
 * The ONLY exception set the grader currently implements: roots (√) and π may stay symbolic. The
 * `partiallyCombined` predicate does not yet gate on the array (it is permissive for both), so the
 * schema pins `allowSymbolic` to exactly this set — carrying `['pi']` alone would misleadingly imply
 * roots are rejected. Relax the pin (and make the predicate honour the array) when a real variant ships.
 */
export const SUPPORTED_SYMBOLIC_EXCEPTIONS: readonly MathSymbolicException[] = ['roots', 'pi'];

/** Numeric Approximation (`approx`) default: required number of digits after the decimal point. */
export const DEFAULT_PRECISION_DIGITS = 2;

/** Bound policies' default constant — seeded when a bound policy is chosen so the field is never empty. */
export const DEFAULT_MATH_BOUND = '0';

/**
 * Exclusivity buckets for combining policies. A blank may combine several policies (AND), but at
 * most ONE un-negated policy per group is meaningful — two would either contradict (e.g. `decimal`
 * ∧ `fraction`, never both) or be redundant (a value basis is implied by every structural policy).
 *  - `valueBasis`  — how the VALUE must match: `equivalent` (exact) vs `approx` (numeric ±N digits).
 *  - `notation`    — how the answer must be WRITTEN: `decimal` / `fraction` / `integer`.
 *  - `form`        — structural constraints: `simplified` (simplest) / `sameForm` (the authored form).
 *  - `lowerBound`  — a `>` / `>=` comparison to an author constant.
 *  - `upperBound`  — a `<` / `<=` comparison to an author constant.
 * (Under `and`, one `lowerBound` + one `upperBound` = a range like `>=1 AND <=10`.)
 */
export type MathPolicyGroup = 'valueBasis' | 'notation' | 'form' | 'lowerBound' | 'upperBound';

export interface MathPolicyMeta {
  /** Exclusivity bucket — at most one un-negated policy per group may be combined under `and`. */
  group: MathPolicyGroup;
  /**
   * May a NOT modifier be applied? Only facets that DON'T carry the value-correctness clause are
   * negatable — negating a value-bearing policy (e.g. `NOT equivalent`) would accept wrong answers.
   */
  negatable: boolean;
  /** Consumes the `precisionDigits` literal field (only `approx`). */
  needsPrecision?: boolean;
  /** Consumes the `bound` literal field — compares the answer to that constant (bound policies). */
  needsBound?: boolean;
  /** Consumes the `allowSymbolic` literal field — which symbolic forms may stay un-evaluated (only `partiallyCombined`). */
  needsSymbolicExceptions?: boolean;
  /**
   * Checks only the answer's FORM/representation, never its value (`combined`, `partiallyCombined`,
   * `reduced`). Alone such a policy would accept a right-form wrong-value answer, so the schema
   * requires it to be paired with a value-bearing term. NOT the inverse of `negatable`, nor implied
   * by `group` (`reduced` is `notation`, `combined` is `form`), so it is its own fact.
   */
  formOnly?: boolean;
}

/**
 * Engine-free metadata table — the SINGLE source of truth for which policies clash. The Zod clash
 * refinement (`validateMathPolicyExpression`), the studio policy builder UI, and any future widget all read
 * from here, so adding a policy means setting its group/negatable once and every layer stays correct.
 */
export const MATH_POLICY_META: Record<MathPolicyId, MathPolicyMeta> = {
  equivalent: { group: 'valueBasis', negatable: false },
  approx: { group: 'valueBasis', negatable: false, needsPrecision: true },
  decimal: { group: 'notation', negatable: true },
  fraction: { group: 'notation', negatable: true },
  integer: { group: 'notation', negatable: true },
  simplified: { group: 'form', negatable: true },
  expanded: { group: 'form', negatable: true },
  sameForm: { group: 'form', negatable: false },
  combined: { group: 'form', negatable: false, formOnly: true },
  partiallyCombined: { group: 'form', negatable: false, formOnly: true, needsSymbolicExceptions: true },
  reduced: { group: 'notation', negatable: true, formOnly: true },
  greaterThan: { group: 'lowerBound', negatable: false, needsBound: true },
  greaterOrEqual: { group: 'lowerBound', negatable: false, needsBound: true },
  lessThan: { group: 'upperBound', negatable: false, needsBound: true },
  lessOrEqual: { group: 'upperBound', negatable: false, needsBound: true }
};

/**
 * One term in a blank's grading expression: a policy, optionally negated, with its own parameters.
 * `precisionDigits` travels with the literal it parametrizes (`approx`) rather than sitting as a
 * sibling field, so each literal is self-contained.
 */
export interface MathPolicyLiteral {
  policy: MathPolicyId;
  negate?: boolean;
  precisionDigits?: number;
  /** Bound policies only: the author constant to compare against (a number/LaTeX string, e.g. `10`). */
  bound?: string;
  /**
   * `partiallyCombined` only: which symbolic forms may stay un-evaluated (currently always the full
   * supported set `['roots', 'pi']`). Recorded so the general policy id identifies its specific variant.
   */
  allowSymbolic?: MathSymbolicException[];
}

/** How a blank's terms combine: `and` (every term must hold) or `or` (any term). */
export const MATH_CONNECTIVES = ['and', 'or'] as const;
export type MathConnective = (typeof MATH_CONNECTIVES)[number];
export const DEFAULT_MATH_CONNECTIVE: MathConnective = 'and';

/**
 * A blank's grading rule: a flat list of (optionally-negated) policy literals joined by ONE
 * connective. Deliberately NOT a nested tree — there is no demonstrated need for grouping like
 * `(A AND B) OR (C AND D)`, and a flat list covers every observed authoring intent
 * (`fraction AND NOT simplified`, `decimal OR fraction`). If grouping is ever needed, this migrates
 * cleanly to a tree (no persisted data). Absent ⇒ the grader applies the default policy.
 */
export interface MathPolicyExpression {
  connective: MathConnective;
  terms: MathPolicyLiteral[];
}

/**
 * Does this grading expression need a stored correct answer? `true` unless EVERY term is a bound
 * policy (which compares to its own constant and ignores the correct answer) — i.e. a purely
 * bound-graded blank ("any value > 10") is answer-less. An absent/empty expression defaults to
 * `equivalent`, which needs an answer.
 */
export function expressionNeedsCorrectAnswer(expression?: MathPolicyExpression): boolean {
  if (!expression?.terms.length) return true;
  return expression.terms.some(term => !MATH_POLICY_META[term.policy]?.needsBound);
}

/** The Studio's מבנה (Form) setting: the right value, or also computed/combined, or combined but √ and π may stay symbolic. */
export const CURATED_MATH_FORMS = ['equivalent', 'combined', 'partiallyCombined'] as const;
export type CuratedMathForm = (typeof CURATED_MATH_FORMS)[number];

/** The Studio's ייצוג מספרי (Numeric representation) setting: any form, or every coefficient in lowest terms. */
export const CURATED_MATH_REPRESENTATIONS = ['any', 'reduced'] as const;
export type CuratedMathRepresentation = (typeof CURATED_MATH_REPRESENTATIONS)[number];

/** The policies the curated Studio controls write — anything else came from outside them. */
const CURATED_POLICIES: ReadonlySet<MathPolicyId> = new Set(['approx', 'combined', 'partiallyCombined', 'reduced']);

/**
 * A blank's grading expression read back as the Studio's curated settings — the one reading both the Studio
 * controls and the player's agent attributes take, so the two cannot describe a blank differently.
 *
 * `precisionDigits` is absent when no `approx` term exists: such a blank grades by exact equivalence. That is
 * every blank stored before the curated controls existed, which the Studio seeds only once an author opens it.
 * `isCurated` is false when the expression holds anything the curated controls cannot write (`or`, a negated
 * term, a bound, a notation or another form policy); the form and representation then describe only part of it.
 */
export function readCuratedMathPolicy(expression?: MathPolicyExpression): {
  form: CuratedMathForm;
  representation: CuratedMathRepresentation;
  precisionDigits?: number;
  isCurated: boolean;
} {
  const terms = expression?.terms ?? [];
  const has = (policy: MathPolicyId) => terms.some(term => term.policy === policy);
  return {
    form: has('partiallyCombined') ? 'partiallyCombined' : has('combined') ? 'combined' : 'equivalent',
    representation: has('reduced') ? 'reduced' : 'any',
    precisionDigits: terms.find(term => term.policy === 'approx')?.precisionDigits,
    isCurated:
      (expression?.connective ?? DEFAULT_MATH_CONNECTIVE) === 'and' &&
      terms.every(term => !term.negate && CURATED_POLICIES.has(term.policy))
  };
}
